/*
 * bc-directional-sticky
 *
 * Directional sticky for the right-hand .product-details column on
 * product pages. When the column is taller than the viewport — which
 * normally leaves the Add-to-Cart button trapped below the fold while
 * .sticky-content--desktop pins the top — this driver dynamically
 * adjusts `top` so the column follows scroll direction:
 *
 *   - scroll DOWN: column scrolls with the page until its bottom is at
 *     the viewport bottom, then sticks there. Buy button is reachable
 *     after one column-height of scroll, no matter how tall the photo
 *     gallery is.
 *   - scroll UP: column scrolls with the page until its top is at the
 *     viewport top (under the sticky header), then sticks there. Title
 *     / price come back into view symmetrically.
 *
 * How it works:
 *   The .product-details element keeps `position: sticky` (from base.css
 *   .sticky-content--desktop). We just update its `top` value on each
 *   scroll event by the same delta as the page scroll, clamped between
 *   the "stick at top" offset (header height) and the "stick at bottom"
 *   offset (viewport_height − column_height − padding, often negative).
 *   While `top` is in the interior of that range, the natural sticky
 *   pin condition ("top of element above sticky top") is not yet met,
 *   so the column scrolls along with the page. The clamps are the two
 *   stuck states.
 *
 * Mobile / short columns: the algorithm short-circuits and restores the
 * CSS default (`top: var(--sticky-header-offset)`) — short columns get
 * regular top-stick, mobile gets no sticky at all (the layout stacks).
 *
 * Variant changes: variant-picker morphs may replace the .product-details
 * element wholesale. We re-query the target whenever the one we hold has
 * been detached (an `isConnected` property read — no layout) and on
 * `variant:update`, so the binding survives morphs.
 *
 * Scroll-frame cost: the per-frame path is pure arithmetic plus at most one
 * `style.top` write. Every layout-dependent input (the header offset, the
 * column height, the viewport height) is measured in _measure() and cached;
 * only an event that can actually change them (resize, a ResizeObserver hit
 * on the column, a target swap, a variant update) marks the cache dirty.
 * Reading them per frame — as this file used to — meant a getComputedStyle
 * + offsetHeight on a large subtree BEFORE writing `top` in the same
 * callback, i.e. a forced synchronous layout every frame, made worse by
 * interleaving with the other bc rAF drivers on the page.
 */
(() => {
  if (customElements.get('bc-directional-sticky')) return;

  class BcDirectionalSticky extends HTMLElement {
    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onMediaChange = this._onMediaChange.bind(this);
      this._onVariantUpdate = this._onVariantUpdate.bind(this);
      this._apply = this._apply.bind(this);
    }

    connectedCallback() {
      this.scope =
        this.closest('.shopify-section') ||
        this.closest('product-information') ||
        document;
      this.mediaQuery = window.matchMedia('(min-width: 750px)');

      this.currentTop = 0;
      this.lastScrollY = window.scrollY;
      this.rafPending = false;
      this.bottomPadding = 16;

      // Cached layout measurements + the last value actually written to
      // style.top, so a frame that computes the same value writes nothing.
      this._maxTop = 0;
      this._minTop = 0;
      this._metricsDirty = true;
      this._appliedTop = null;

      this._findTarget();

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this.mediaQuery.addEventListener('change', this._onMediaChange);
      document.addEventListener('variant:update', this._onVariantUpdate);

      // Watch the column itself so variant-driven height changes (option
      // groups appearing/disappearing) trigger a re-clamp instead of
      // stranding the column at a stale `top` value.
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this._onResize);
        if (this.target) this.ro.observe(this.target);
      }

      // `--sticky-header-offset` is SCROLL-state driven, not layout driven:
      // product-information-content.liquid sets it to the header height only
      // while `#header-component[data-sticky-state='active']`, and header.js
      // flips that attribute from its own scroll observer — no resize event,
      // no ResizeObserver hit on the column. Watch the attribute (and the
      // header's own size, since --header-height is republished from there)
      // so the cached max top follows the header showing/hiding.
      const header = document.querySelector('#header-component');
      if (header) {
        if (window.MutationObserver) {
          this.headerMo = new MutationObserver(this._onResize);
          this.headerMo.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });
        }
        if (window.ResizeObserver) {
          this.headerRo = new ResizeObserver(this._onResize);
          this.headerRo.observe(header);
        }
      }

      // Start in the top-stuck state. If the user lands on the page
      // already scrolled past the top, the first scroll event will
      // begin nudging `currentTop` toward the bottom-stuck end of
      // the range. (_findTarget already measured and anchored when a
      // target was found; this covers the target-less case.)
      if (this._metricsDirty) this._measure();
      this.currentTop = this._maxTop;
      this._scheduleApply();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this.mediaQuery?.removeEventListener('change', this._onMediaChange);
      document.removeEventListener('variant:update', this._onVariantUpdate);
      this.ro?.disconnect();
      this.ro = null;
      this.headerMo?.disconnect();
      this.headerMo = null;
      this.headerRo?.disconnect();
      this.headerRo = null;
      if (this.target) this.target.style.top = '';
      this._appliedTop = null;
    }

    _findTarget() {
      const next = this.scope?.querySelector('.product-details.sticky-content--desktop');
      if (next === this.target) return;
      // The old target is going away (or being replaced); release our
      // `top` override so the new element starts from CSS defaults.
      if (this.target) this.target.style.top = '';
      this.target = next || null;
      // The override we were tracking belonged to the old element.
      this._appliedTop = null;
      this._metricsDirty = true;
      if (this.target) {
        // Re-anchor to the top-stuck position. Without this, currentTop
        // would carry over from the previous (now-detached) element —
        // if the user scrolled mid-morph, the delta would be stranded
        // and the new element lands at a stale offset.
        this._measure();
        this.currentTop = this._maxTop;
        this.lastScrollY = window.scrollY;
      }
      if (this.ro) {
        this.ro.disconnect();
        if (this.target) this.ro.observe(this.target);
      }
    }

    /**
     * The one place layout is read. `--sticky-header-offset` is the CSS
     * default `top` (the top-stuck state) and the column height + viewport
     * height give the bottom-stuck state. Both change only with layout, so
     * they are cached here and refreshed on the events that can move them —
     * never from the scroll path, where the read would sit between other
     * components' style writes and force a synchronous layout.
     */
    _measure() {
      this._metricsDirty = false;
      if (!this.target) {
        this._maxTop = 0;
        this._minTop = 0;
        return;
      }
      const style = getComputedStyle(this.target);
      this._maxTop = parseFloat(style.getPropertyValue('--sticky-header-offset')) || 0;
      this._minTop = window.innerHeight - this.target.offsetHeight - this.bottomPadding;
    }

    /**
     * Mark the cached measurements stale and let the next apply pass
     * refresh them. No debounce timer needed: _scheduleApply already
     * coalesces to one rAF, so a resize storm re-measures once per frame
     * at worst, and the measurement happens at the top of that frame's
     * callback rather than between writes.
     */
    _invalidate() {
      this._metricsDirty = true;
      this._scheduleApply();
    }

    /** Single style write per frame, skipped when the value is unchanged. */
    _writeTop(value) {
      if (!this.target || this._appliedTop === value) return;
      this._appliedTop = value;
      this.target.style.top = value;
    }

    _onScroll() {
      if (!this.target || !this.mediaQuery.matches) {
        // Still track lastScrollY so the next eligible scroll computes
        // a sensible delta instead of jumping the whole scroll distance
        // at once.
        this.lastScrollY = window.scrollY;
        return;
      }
      const newScrollY = window.scrollY;
      const delta = newScrollY - this.lastScrollY;
      this.lastScrollY = newScrollY;
      this.currentTop -= delta;
      this._scheduleApply();
    }

    _onResize() { this._invalidate(); }
    _onMediaChange() { this._invalidate(); }

    _onVariantUpdate() {
      // Wait a frame so the variant-picker morph (which can replace
      // .product-details, and in any case changes its height) is committed
      // before we re-bind and re-measure.
      requestAnimationFrame(() => {
        this._findTarget();
        this._invalidate();
      });
    }

    _scheduleApply() {
      if (this.rafPending) return;
      this.rafPending = true;
      requestAnimationFrame(this._apply);
    }

    _apply() {
      this.rafPending = false;
      // Only re-query when the element we hold has left the document (a
      // morph replaced it). `isConnected` is a plain property read; the
      // querySelector this replaces ran on every scroll frame.
      if (!this.target || !this.target.isConnected) this._findTarget();
      if (!this.target) return;

      if (!this.mediaQuery.matches) {
        // Mobile: layout stacks, no sticky behavior wanted. Clear our
        // override so the upstream CSS default takes back over.
        this._writeTop('');
        return;
      }

      if (this._metricsDirty) this._measure();
      const maxTop = this._maxTop;
      const minTop = this._minTop;

      if (minTop >= maxTop) {
        // Column fits in the viewport (with breathing room) — regular
        // top-stick is the right behavior. Clear our override so the
        // base.css rule (`top: var(--sticky-header-offset)`) wins, and
        // keep `currentTop` parked at the top-stuck value so the next
        // overflow situation starts from a sane state.
        this._writeTop('');
        this.currentTop = maxTop;
        return;
      }

      // Clamp into the two stuck states.
      if (this.currentTop < minTop) this.currentTop = minTop;
      if (this.currentTop > maxTop) this.currentTop = maxTop;

      // Once clamped, continued scrolling in the same direction produces
      // the same value frame after frame — _writeTop drops those.
      this._writeTop(`${this.currentTop}px`);
    }
  }

  customElements.define('bc-directional-sticky', BcDirectionalSticky);
})();
